import { defaultAbiCoder, Interface } from '@ethersproject/abi'
import { BigNumber } from '@ethersproject/bignumber'
import { Percent } from '@uniswap/sdk-core'
import { ZERO_ADDRESS } from 'constants/misc'

export const UniversalRouterCommands = {
  V3_SWAP_EXACT_IN: 0x00,
  V3_SWAP_EXACT_OUT: 0x01,
  PERMIT2_TRANSFER_FROM: 0x02,
  SWEEP: 0x04,
  PAY_PORTION: 0x06,
  V2_SWAP_EXACT_IN: 0x08,
  V2_SWAP_EXACT_OUT: 0x09,
  WRAP_ETH: 0x0b,
  UNWRAP_WETH: 0x0c,
} as const

const CONTRACT_BALANCE = BigNumber.from(2).pow(255)

const SWAP_EXACT_IN_COMMANDS: Set<number> = new Set([
  UniversalRouterCommands.V3_SWAP_EXACT_IN,
  UniversalRouterCommands.V2_SWAP_EXACT_IN,
])

const SWAP_INPUT_TYPES: Record<number, string[]> = {
  [UniversalRouterCommands.V3_SWAP_EXACT_IN]: ['address', 'uint256', 'uint256', 'bytes', 'bool'],
  [UniversalRouterCommands.V3_SWAP_EXACT_OUT]: ['address', 'uint256', 'uint256', 'bytes', 'bool'],
  [UniversalRouterCommands.V2_SWAP_EXACT_IN]: ['address', 'uint256', 'uint256', 'address[]', 'bool'],
  [UniversalRouterCommands.V2_SWAP_EXACT_OUT]: ['address', 'uint256', 'uint256', 'address[]', 'bool'],
}

const UNIVERSAL_ROUTER_EXECUTE_ABI = [
  'function execute(bytes calldata commands, bytes[] calldata inputs, uint256 deadline) external payable',
]

const universalRouterInterface = new Interface(UNIVERSAL_ROUTER_EXECUTE_ABI)

export interface FeeConfig {
  fee: Percent
  recipient: string
}

export interface InjectFeeOptions {
  calldata: string
  inputToken: InputTokenInfo
  feeConfig: FeeConfig
  routerAddress: string
}

export interface InputTokenInfo {
  address: string
  isNative: boolean
}

interface DecodedExecuteCall {
  commands: string
  inputs: string[]
  deadline: string
}

function decodeExecuteCalldata(calldata: string): DecodedExecuteCall {
  const decoded = universalRouterInterface.decodeFunctionData('execute', calldata)

  return {
    commands: decoded.commands as string,
    inputs: [...(decoded.inputs as string[])],
    deadline: decoded.deadline.toString(),
  }
}

function encodeExecuteCalldata(commands: string, inputs: string[], deadline: string): string {
  return universalRouterInterface.encodeFunctionData('execute', [commands, inputs, deadline])
}

function encodePayPortionInput(tokenAddress: string, recipient: string, feeBips: string): string {
  return defaultAbiCoder.encode(['address', 'address', 'uint256'], [tokenAddress, recipient, feeBips])
}

function encodePermit2TransferFromInput(tokenAddress: string, recipient: string, amount: BigNumber): string {
  return defaultAbiCoder.encode(['address', 'address', 'uint160'], [tokenAddress, recipient, amount])
}

function encodeSweepInput(tokenAddress: string, recipient: string, amountMin: BigNumber = BigNumber.from(0)): string {
  return defaultAbiCoder.encode(['address', 'address', 'uint256'], [tokenAddress, recipient, amountMin])
}

function findWrapEthIndex(commands: number[]): number {
  for (let i = 0; i < commands.length; i++) {
    if ((commands[i] & 0x3f) === UniversalRouterCommands.WRAP_ETH) {
      return i
    }
  }
  return -1
}

function findUnwrapWethIndex(commands: number[]): number {
  for (let i = 0; i < commands.length; i++) {
    if ((commands[i] & 0x3f) === UniversalRouterCommands.UNWRAP_WETH) {
      return i
    }
  }
  return -1
}

function decodeWrapEthInput(input: string): { recipient: string; amount: BigNumber } {
  const decoded = defaultAbiCoder.decode(['address', 'uint256'], input)
  return {
    recipient: decoded[0] as string,
    amount: decoded[1] as BigNumber,
  }
}

function encodeWrapEthInput(recipient: string, amount: BigNumber): string {
  return defaultAbiCoder.encode(['address', 'uint256'], [recipient, amount])
}

function decodeUnwrapWethInput(input: string): { recipient: string; amountMin: BigNumber } {
  const decoded = defaultAbiCoder.decode(['address', 'uint256'], input)
  return {
    recipient: decoded[0] as string,
    amountMin: decoded[1] as BigNumber,
  }
}

function encodeUnwrapWethInput(recipient: string, amountMin: BigNumber): string {
  return defaultAbiCoder.encode(['address', 'uint256'], [recipient, amountMin])
}

function calculateFeeAmount(totalAmount: BigNumber, feeBips: number): BigNumber {
  return totalAmount.mul(feeBips).div(10000)
}

function percentToBips(percent: Percent): string {
  return percent.multiply(10000).quotient.toString()
}

function parseCommands(commandsHex: string): number[] {
  const hex = commandsHex.startsWith('0x') ? commandsHex.slice(2) : commandsHex
  const commands: number[] = []
  for (let i = 0; i < hex.length; i += 2) {
    commands.push(parseInt(hex.slice(i, i + 2), 16))
  }
  return commands
}

function commandsToHex(commands: number[]): string {
  return '0x' + commands.map((c) => c.toString(16).padStart(2, '0')).join('')
}

function findSwapCommandIndex(commands: number[]): number {
  for (let i = 0; i < commands.length; i++) {
    const command = commands[i] & 0x3f // Remove flags
    if (SWAP_EXACT_IN_COMMANDS.has(command)) {
      return i
    }
  }
  return -1
}

function hasWrapEthCommand(commands: number[]): boolean {
  return commands.some((cmd) => (cmd & 0x3f) === UniversalRouterCommands.WRAP_ETH)
}

function extractAmountInFromSwap(command: number, input: string): BigNumber {
  const commandType = command & 0x3f
  const inputTypes = SWAP_INPUT_TYPES[commandType]

  if (!inputTypes) {
    throw new Error(`Unknown swap command type: ${commandType}`)
  }

  const decoded = defaultAbiCoder.decode(inputTypes, input)
  return decoded[1] as BigNumber
}

function modifySwapToUseContractBalance(
  command: number,
  input: string,
  feeMultiplier?: { numerator: number; denominator: number }
): string {
  const commandType = command & 0x3f
  const inputTypes = SWAP_INPUT_TYPES[commandType]

  if (!inputTypes) {
    return input
  }

  const decoded = defaultAbiCoder.decode(inputTypes, input)
  const decodedArray = [...decoded]

  // Second parameter (index 1) is amountIn - set to CONTRACT_BALANCE
  decodedArray[1] = CONTRACT_BALANCE

  // If fee is taken from input, adjust amountOutMin proportionally
  // to account for reduced input amount
  if (feeMultiplier) {
    const originalAmountOutMin = decodedArray[2] as BigNumber
    decodedArray[2] = originalAmountOutMin.mul(feeMultiplier.numerator).div(feeMultiplier.denominator)
  }

  return defaultAbiCoder.encode(inputTypes, decodedArray)
}

function modifySwapForRouterPayer(
  command: number,
  input: string,
  feeMultiplier?: { numerator: number; denominator: number }
): string {
  const commandType = command & 0x3f
  const inputTypes = SWAP_INPUT_TYPES[commandType]

  if (!inputTypes) {
    return input
  }

  const decoded = defaultAbiCoder.decode(inputTypes, input)
  const decodedArray = [...decoded]

  // Set amountIn to CONTRACT_BALANCE (index 1)
  decodedArray[1] = CONTRACT_BALANCE
  // Set payerIsUser to false (index 4) - router has the tokens
  decodedArray[4] = false

  // If fee is taken from input, adjust amountOutMin proportionally
  // to account for reduced input amount
  if (feeMultiplier) {
    const originalAmountOutMin = decodedArray[2] as BigNumber
    decodedArray[2] = originalAmountOutMin.mul(feeMultiplier.numerator).div(feeMultiplier.denominator)
  }

  return defaultAbiCoder.encode(inputTypes, decodedArray)
}

/**
 * Injects interface fee from input token into Universal Router calldata.
 *
 * For native input (WRAP_ETH present):
 *   Fee taken in NATIVE ETH:
 *   WRAP_ETH (99%) → V3_SWAP (CONTRACT_BALANCE) → SWEEP native ETH (1% fee)
 *
 * For ERC20 input:
 *   Fee taken from input token:
 *   PERMIT2_TRANSFER_FROM → PAY_PORTION → V3_SWAP (CONTRACT_BALANCE, payerIsUser=false)
 */
export function injectInputFee(options: InjectFeeOptions): string {
  const { calldata, inputToken, feeConfig, routerAddress } = options
  const { commands, inputs, deadline } = decodeExecuteCalldata(calldata)

  const commandBytes = parseCommands(commands)
  const swapIndex = findSwapCommandIndex(commandBytes)

  if (swapIndex === -1) {
    throw new Error('No swap command found in calldata')
  }

  const feeBips = percentToBips(feeConfig.fee)
  const isNativeInput = hasWrapEthCommand(commandBytes)

  if (isNativeInput) {
    // Native input: Take fee in NATIVE ETH (not wrapped)
    // 1. Modify WRAP_ETH to wrap only (100% - fee%)
    // 2. V3_SWAP with CONTRACT_BALANCE (swaps all wrapped ETH)
    // 3. SWEEP native ETH to fee recipient (the fee portion that wasn't wrapped)

    const wrapEthIndex = findWrapEthIndex(commandBytes)
    if (wrapEthIndex === -1) {
      throw new Error('WRAP_ETH command not found')
    }

    const { recipient: wrapRecipient, amount: originalAmount } = decodeWrapEthInput(inputs[wrapEthIndex])
    const feeBipsNum = parseInt(feeBips, 10)
    const feeAmount = calculateFeeAmount(originalAmount, feeBipsNum)
    const amountAfterFee = originalAmount.sub(feeAmount)

    const modifiedWrapEthInput = encodeWrapEthInput(wrapRecipient, amountAfterFee)

    // Adjust amountOutMin proportionally: (10000 - feeBips) / 10000
    const feeMultiplier = { numerator: 10000 - feeBipsNum, denominator: 10000 }
    const modifiedSwapInput = modifySwapToUseContractBalance(commandBytes[swapIndex], inputs[swapIndex], feeMultiplier)

    const sweepNativeInput = encodeSweepInput(ZERO_ADDRESS, feeConfig.recipient, feeAmount)

    const newCommandBytes = [...commandBytes, UniversalRouterCommands.SWEEP]

    const newInputs = inputs.map((input, idx) => {
      if (idx === wrapEthIndex) return modifiedWrapEthInput
      if (idx === swapIndex) return modifiedSwapInput
      return input
    })
    newInputs.push(sweepNativeInput)

    return encodeExecuteCalldata(commandsToHex(newCommandBytes), newInputs, deadline)
  } else {
    // ERC20 input: Need to transfer tokens to router first via PERMIT2_TRANSFER_FROM
    // Then PAY_PORTION, then swap with payerIsUser=false
    const feeBipsNum = parseInt(feeBips, 10)
    const originalAmountIn = extractAmountInFromSwap(commandBytes[swapIndex], inputs[swapIndex])
    const transferInput = encodePermit2TransferFromInput(inputToken.address, routerAddress, originalAmountIn)
    const payPortionInput = encodePayPortionInput(inputToken.address, feeConfig.recipient, feeBips)
    // Adjust amountOutMin proportionally: (10000 - feeBips) / 10000
    const feeMultiplier = { numerator: 10000 - feeBipsNum, denominator: 10000 }
    const modifiedSwapInput = modifySwapForRouterPayer(commandBytes[swapIndex], inputs[swapIndex], feeMultiplier)

    // If the route unwraps WETH -> native at the end, the UNWRAP minimum must be
    // reduced consistently with the swap's amountOutMin, otherwise the router can
    // revert with InsufficientETH() even if the swap would satisfy the modified minOut.
    const unwrapIndex = findUnwrapWethIndex(commandBytes)
    const modifiedUnwrapInput =
      unwrapIndex === -1
        ? undefined
        : (() => {
            const { recipient, amountMin } = decodeUnwrapWethInput(inputs[unwrapIndex])
            const newAmountMin = amountMin.mul(feeMultiplier.numerator).div(feeMultiplier.denominator)
            return encodeUnwrapWethInput(recipient, newAmountMin)
          })()

    const newCommandBytes = [
      ...commandBytes.slice(0, swapIndex),
      UniversalRouterCommands.PERMIT2_TRANSFER_FROM,
      UniversalRouterCommands.PAY_PORTION,
      ...commandBytes.slice(swapIndex),
    ]

    const newInputs = inputs.map((input, idx) => {
      if (idx === swapIndex) return modifiedSwapInput
      if (modifiedUnwrapInput && idx === unwrapIndex) return modifiedUnwrapInput
      return input
    })
    newInputs.splice(swapIndex, 1, transferInput, payPortionInput, modifiedSwapInput)

    return encodeExecuteCalldata(commandsToHex(newCommandBytes), newInputs, deadline)
  }
}

export function getInputTokenInfo(trade: {
  inputAmount: {
    currency: {
      isNative: boolean
      address?: string
      wrapped?: { address: string }
    }
  }
}): InputTokenInfo {
  const { currency } = trade.inputAmount

  if (currency.isNative) {
    if (!currency.wrapped?.address) {
      throw new Error('Native currency must have a wrapped token address')
    }
    return {
      isNative: true,
      address: currency.wrapped.address,
    }
  }

  if (!currency.address) {
    throw new Error('Token currency must have an address')
  }

  return {
    isNative: false,
    address: currency.address,
  }
}
